/* eslint-disable require-jsdoc */
import React, { useEffect, useRef, useState } from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import Editor from "ckeditor5-custom-build/build/ckeditor";
import { createPortal } from "react-dom";

import useFileUpload from "../../../../hooks/useFileUpload";

class MyUploadAdapter {
  constructor(props) {
    const { loader, uploadFile } = props || {};
    this.loader = loader;
    this.uploadFile = uploadFile;
  }

  upload() {
    return this.loader.file.then(async file => {
      const result = await this.uploadFile({ file, isPublic: true });
      if (result.success) {
        return { default: result.url };
      }
      throw result.error || "Upload failed";
    });
  }
}

const MergeTags = props => {
  const { options, handleInsert } = props;
  const [active, setActive] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    // add event listener for click outside
    document.addEventListener("click", handleClickOutside, true);
    // remove event listener on cleanup
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, []);

  const handleClickOutside = e => {
    // if the click was outside the dropdown, set active to false
    if (ref.current && !ref.current.contains(e.target)) setActive(false);
  };

  const handleClick = e => {
    // if the click was inside the dropdown, set active
    if (ref.current.contains(e.target)) setActive(true);
  };

  return (
    <div ref={ref} className="ck ck-dropdown ck-heading-dropdown">
      <button
        type="button"
        className={`ck ck-button ${!active ? "ck-off" : "ck-on"} ck-button_with-text ck-dropdown__button`}
        onClick={handleClick}
      >
        <span
          className="ck ck-button__label"
          id="ck-editor__aria-label_e774bec3de7ea3b225cd785b1caf2cbf6"
        >
          Merge Tags
        </span>
        <svg
          className="ck ck-icon ck-reset_all-excluded ck-icon_inherit-color ck-dropdown__arrow"
          viewBox="0 0 10 10"
        >
          <path d="M.941 4.523a.75.75 0 1 1 1.06-1.06l3.006 3.005 3.005-3.005a.75.75 0 1 1 1.06 1.06l-3.549 3.55a.75.75 0 0 1-1.168-.136L.941 4.523z"></path>
        </svg>
      </button>
      <div
        className={`ck ck-reset ck-dropdown__panel ck-dropdown__panel_se ${!active ? "" : "ck-dropdown__panel-visible"}`}
      >
        <ul className="ck ck-reset ck-list">
          {options?.map((option, index) => {
            return (
              <li
                key={index}
                className="ck ck-list__item"
                onClick={() => {
                  setActive(false);
                  handleInsert?.(option?.value);
                }}
              >
                <button
                  type="button"
                  className="ck ck-button ck-heading_paragraph ck-off ck-button_with-text"
                >
                  <span className="ck ck-button__label">{option?.text}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

const CKEditorText = ({ formik, ...props }) => {
  const { uploadFile } = useFileUpload();
  const [readyEditor, setReadyEditor] = useState(null);

  const {
    className,
    error = "",
    label,
    mailMergeOptions,
    name,
    onBlur,
    onChange,
    value,
    variant,
    mergeTags = true,
  } = props;
  const variants = {
    default: {
      label: "block text-sm font-medium text-black mb-2",
      wrapper: "border border-black rounded",
      editor: "h-[220px] overflow-auto m-0 p-3 py-2 sm:h-[300px] sm:p-4",
    },
    simpleRight: {
      label: "block text-xl font-bold font-medium text-black mb-4",
      wrapper: "",
      editor: "h-[220px] overflow-auto m-0 p-3 py-2 sm:h-[300px] sm:p-4",
    },
  };
  const classes = variants[variant] || variants.default;
  return (
    <>
      <div
        className={`z-0 flex min-w-0 max-w-full flex-1 flex-col ${className || ""}`}
      >
        {label && <label className={classes.label}>{label}</label>}
        <CKEditor
          editor={Editor}
          config={{
            simpleUpload: {
              // The URL that the images are uploaded to.
              uploadUrl: "http://example.com",

              // Enable the XMLHttpRequest.withCredentials property.
              withCredentials: true,

              // Headers sent along with the XMLHttpRequest to the upload server.
              headers: {
                "X-CSRF-TOKEN": "CSRF-Token",
                Authorization: "Bearer <JSON Web Token>",
              },
            },
          }}
          data={value}
          onReady={editor => {
            editor.editing.view.change(writer => {
              // writer.setStyle('height', `${400}px`, editor.editing.view.document.getRoot());
              // writer.setStyle('resize', 'vertical', editor.editing.view.document.getRoot());
            });

            editor.plugins.get("FileRepository").createUploadAdapter =
              loader => {
                return new MyUploadAdapter({ loader, uploadFile });
              };

            setReadyEditor(editor);
          }}
          onChange={(event, editor) => {
            const data = editor.getData();
            onChange(data);
          }}
          onBlur={(event, editor) => {
            const data = editor.getData();
            onChange(data);
            onBlur?.({ target: { name } });
          }}
        />
        {mergeTags &&
          readyEditor &&
          createPortal(
            <MergeTags
              options={mailMergeOptions}
              handleInsert={value => {
                readyEditor.model.change(writer => {
                  readyEditor.model.insertContent(writer.createText(value));
                });
                readyEditor.editing.view.focus();
              }}
            />,
            readyEditor.ui.view.toolbar.element.querySelector(
              ".ck-toolbar__items",
            ),
          )}
        {error && <small className="text-sm text-red-600">{error}</small>}
      </div>
    </>
  );
};

export default CKEditorText;
